import React, { useState, useEffect, useMemo } from "react";
import { withRouter, useParams } from "react-router-dom";
import { connect, useDispatch, useSelector } from "react-redux";
import { compose } from "redux";
import { withTranslation } from "react-i18next";
import { createStructuredSelector } from "reselect";
import styled from "styled-components";

import ACTION from "../store/action";
import {
  selectIsFetching,
  selectFilterList,
  selectFilterListStatuses,
  selectIsFilterListFiltersModified,
  selectTableColumnsModified,
  selectAllPatientsData,
  selectVisitScheduleData,
  selectHighPrioritiesData
} from "../store/selector";
import { AllPatientsTable } from "../component/TableSection/AllPatientsTable";
import { VisitScheduleTable } from "../component/TableSection/VisitScheduleTable";
import { HighPrioritiesTable } from "../component/TableSection/HighPrioritiesTable";
import { BulkActionsContainer } from "../component/BulkActions";
import { PendingRenewalTable } from "../component/TableSection/PendingRenewalTable";
import { AllApplicationsTable } from "../component/TableSection/AllApplicationsTable";
import { FilterListControls } from "../component/FilterList/FilterListControls";
import { ApiNames } from "../api/api";
import TableFilters from "../component/TableFilters";
import { selectedViews } from "../constant/table";
import { FilterListForm } from "../component/FormPopups/FilterListForm";
import { AllClaimsTable } from "../component/TableSection/AllClaimsTable";
import ACTIONS from "../store/action";
import { WHITE, GRAY_100 } from "../constant/colors";
import { Skeleton } from "antd";

const DashboardScreen = styled.div`
  margin: 0px 15px 0px 15px;
  position: relative;
  background: ${WHITE};
  border-radius: 10px;
  padding: 10px 15px 0px 15px;
`;

const FilterSectionWrapper = styled.div`
  flex: 0 0 288px;
  max-width: ${(props) => {
    if (props.filtersVisible) {
      return "288px";
    }
    return "0px";
  }};
  transition: max-width 0.3s ease;
`;

const TableSectionWrapper = styled.div`
  flex: 1 1 auto;
  overflow: hidden;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${GRAY_100};
  margin: 5px 0;
`;

const DEFAULT_PAGE = 1;
const DEFAULT_SORTER = {};

const FilterListViewComponent = ({
  isFetching,
  searchSavedListByIdAct,
  filterList,
  filterListStatuses,
  markSavedFilterAsChanged,
  filterListFiltersModified,
  tableColumnsModified,
  updateSavedFilterViewParameters,
  clearSorterAct
}) => {
  const dispatch = useDispatch();
  const [filtersVisible, setFiltersVisible] = useState(false);
  const filterListId = parseInt(useParams().filterListId);
  const updateSavedFilterViewParametersAct = updateSavedFilterViewParameters(filterListId);
  const clearTablesDataAct = (selectedView) => dispatch(ACTION.clearTablesData(selectedView));

  // Get data for bulk selection
  const allPatientsData = useSelector(selectAllPatientsData);
  const visitScheduleData = useSelector(selectVisitScheduleData);
  const highPrioritiesData = useSelector(selectHighPrioritiesData);

  // Extract visible patient IDs based on the selected view
  const visiblePatientIds = useMemo(() => {
    if (filterList?.selectedView === selectedViews.allPatients) {
      return (allPatientsData?.patients || []).map((patient) => patient.patientId).filter(Boolean);
    }
    if (filterList?.selectedView === selectedViews.visitSchedule) {
      return (visitScheduleData?.patients || []).map((patient) => patient.patientId).filter(Boolean);
    }
    if (filterList?.selectedView === selectedViews.highPriority) {
      return (highPrioritiesData?.patients || []).map((patient) => patient.patientId).filter(Boolean);
    }
    return [];
  }, [filterList?.selectedView, allPatientsData?.patients, visitScheduleData?.patients, highPrioritiesData?.patients]);

  // Get total records for the current view
  const totalRecords = useMemo(() => {
    if (filterList?.selectedView === selectedViews.allPatients) {
      return allPatientsData?.totalPatients || 0;
    }
    if (filterList?.selectedView === selectedViews.visitSchedule) {
      return visitScheduleData?.totalPatients || 0;
    }
    if (filterList?.selectedView === selectedViews.highPriority) {
      return highPrioritiesData?.totalPatients || 0;
    }
    return 0;
  }, [
    filterList?.selectedView,
    allPatientsData?.totalPatients,
    visitScheduleData?.totalPatients,
    highPrioritiesData?.totalPatients
  ]);

  useEffect(() => {
    if (filterListId && filterListStatuses.id !== filterListId) {
      //Note: Reset the filter parameters when changing the view to another filter page.
      updateSavedFilterViewParametersAct(DEFAULT_PAGE, DEFAULT_SORTER);
    }

    // Note: we need to fetch data always to make data consistent for the user.
    searchSavedListByIdAct(filterListId);

    if (filterListId) {
      // Note: Reset the filter modified status to mark it as not modified.
      markSavedFilterAsChanged(filterListId, false);
    }
  }, [filterListId]);

  useEffect(() => {
    // Clear table sorting when accessing saved list to ensure clean state on entry
    if (filterList?.selectedView) {
      clearSorterAct(filterList.selectedView);
    }
  }, [filterListId, filterList?.selectedView]);

  useEffect(() => {
    // Clear all table data when switching to a new saved list
    if (filterList?.selectedView && (!filterListId || (filterListId && filterListStatuses.id !== filterListId))) {
      clearTablesDataAct(filterList?.selectedView);
    }

    if (filterList?.id && (tableColumnsModified || filterListFiltersModified)) {
      markSavedFilterAsChanged(filterList?.id, true);
    }
  }, [tableColumnsModified, filterListFiltersModified]);

  return (
    <DashboardScreen direction={"vertical"}>
      {!isFetching ? (
        <>
          <FilterListControls searchSavedListByIdAct={searchSavedListByIdAct} filterListId={filterListId} />
          <Divider />
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-start",
              transition: "all 0.3s ease"
            }}
          >
            <FilterSectionWrapper filtersVisible={filtersVisible}>
              <TableFilters
                filtersVisible={filtersVisible}
                setFiltersVisible={setFiltersVisible}
                showSaveNewListBtn={false}
                isFilterListView={true}
              />
            </FilterSectionWrapper>
            <TableSectionWrapper>
              {filterList?.selectedView === selectedViews.allPatients && (
                <BulkActionsContainer
                  viewKey="all-patients"
                  entityType="patient"
                  visibleRecordIds={visiblePatientIds}
                  totalRecords={totalRecords}
                  rowKey="patientId"
                >
                  {({ rowSelection }) => (
                    <AllPatientsTable
                      isSearch={false}
                      updateSavedFilterViewParameters={updateSavedFilterViewParametersAct}
                      isFilterListView={true}
                      rowSelection={rowSelection}
                    />
                  )}
                </BulkActionsContainer>
              )}
              {filterList?.selectedView === selectedViews.visitSchedule && (
                <BulkActionsContainer
                  viewKey="visit-schedule"
                  entityType="patient"
                  visibleRecordIds={visiblePatientIds}
                  totalRecords={totalRecords}
                  rowKey="patientId"
                >
                  {({ rowSelection }) => (
                    <VisitScheduleTable
                      updateSavedFilterViewParameters={updateSavedFilterViewParametersAct}
                      isFilterListView={true}
                      rowSelection={rowSelection}
                    />
                  )}
                </BulkActionsContainer>
              )}
              {filterList?.selectedView === selectedViews.pendingRenewal && (
                <PendingRenewalTable
                  updateSavedFilterViewParameters={updateSavedFilterViewParametersAct}
                  isFilterListView={true}
                />
              )}
              {filterList?.selectedView === selectedViews.highPriority && (
                <BulkActionsContainer
                  viewKey="high-priority"
                  entityType="patient"
                  visibleRecordIds={visiblePatientIds}
                  totalRecords={totalRecords}
                  rowKey="patientId"
                >
                  {({ rowSelection }) => (
                    <HighPrioritiesTable
                      updateSavedFilterViewParameters={updateSavedFilterViewParametersAct}
                      isFilterListView={true}
                      rowSelection={rowSelection}
                    />
                  )}
                </BulkActionsContainer>
              )}
              {filterList?.selectedView === selectedViews.allApplications && (
                <AllApplicationsTable
                  updateSavedFilterViewParameters={updateSavedFilterViewParametersAct}
                  isFilterListView={true}
                />
              )}
              {filterList?.selectedView === selectedViews.allClaims && (
                <AllClaimsTable
                  updateSavedFilterViewParameters={updateSavedFilterViewParametersAct}
                  isFilterListView={true}
                />
              )}
            </TableSectionWrapper>
            <FilterListForm />
          </div>
        </>
      ) : (
        <>
          <Skeleton.Button style={{ width: 100, height: 40 }} active />
          <Skeleton style={{ paddingTop: 16 }} active paragraph={{ rows: 6 }} />
          <Skeleton style={{ paddingTop: 32 }} active paragraph={{ rows: 6 }} />
        </>
      )}
    </DashboardScreen>
  );
};

const mapDispatchToProps = (dispatch) => ({
  searchSavedListByIdAct: (filterListId) => dispatch(ACTION.searchSavedListByIdAction(filterListId)),
  markSavedFilterAsChanged: (filterListID, isModified) =>
    dispatch(ACTIONS.markSavedFilterAsChanged(filterListID, isModified)),
  updateSavedFilterViewParameters: (filterListID) => (page, sorter) =>
    dispatch(ACTION.updateSavedFilterViewParameters(filterListID, page, sorter)),
  clearSorterAct: (view) => dispatch(ACTION.clearSorterAction(view))
});
const mapStateToProps = createStructuredSelector({
  isFetching: selectIsFetching([ApiNames.FindSavedListById]),
  filterList: selectFilterList,
  filterListStatuses: selectFilterListStatuses,
  filterListFiltersModified: selectIsFilterListFiltersModified,
  tableColumnsModified: selectTableColumnsModified
});

const FilterListView = compose(
  withTranslation(),
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(FilterListViewComponent);
export { FilterListView };
